// client/src/pages/BecomeSeller.js
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Container, Card, CardContent, Typography, TextField, Button, Alert, Stack, Chip, CircularProgress } from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { API_BASE_URL } from '../utils/media';

const statusColor = { pending: 'warning', approved: 'success', rejected: 'error' };

const BecomeSeller = () => {
  const { user, token } = useContext(AuthContext);
  const { store } = useStore();

  const [application, setApplication] = useState(undefined); // undefined = loading, null = none yet
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ storeName: '', description: '', contactPhone: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchApplication = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller-applications/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setApplication(data);
    } catch (err) {
      setApplication(null);
    }
  }, [token]);

  useEffect(() => { fetchApplication(); }, [fetchApplication]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to submit application.');
      setMessage(data.message);
      setShowForm(false);
      fetchApplication();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Become a Seller</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          You'll need an account before you can apply to sell on {store.name}.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button component={Link} to="/login" variant="contained">Login</Button>
          <Button component={Link} to="/register" variant="outlined">Create an Account</Button>
        </Stack>
      </Container>
    );
  }

  if (user.role === 'seller' || user.role === 'admin') {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>You're already set up to sell</Typography>
        <Button component={Link} to="/profile" variant="contained" sx={{ mt: 2 }}>Go to My Products</Button>
      </Container>
    );
  }

  if (application === undefined) {
    return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  // Show the form when there's no application yet, or the applicant chose to re-apply after a rejection.
  const displayForm = !application || showForm;

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>Become a Seller</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You're applying with your existing account ({user.email}). Fill in the form below and an admin will review your application before your store goes live.
          </Typography>

          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

          {application && (
            <Stack spacing={1.5} sx={{ mb: displayForm ? 3 : 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography fontWeight={600}>Application status:</Typography>
                <Chip label={application.status} color={statusColor[application.status]} size="small" sx={{ textTransform: 'capitalize' }} />
              </Stack>
              <Typography><strong>Store name:</strong> {application.store_name}</Typography>
              <Typography color="text.secondary">
                Submitted {new Date(application.created_at).toLocaleDateString()}
              </Typography>

              {application.status === 'pending' && (
                <Alert severity="info">
                  Your application is under review. We'll let you know once an admin has made a decision.
                </Alert>
              )}
              {application.status === 'approved' && (
                <Alert severity="success">
                  Your application was approved! Log out and back in to access your seller tools.
                </Alert>
              )}
              {application.status === 'rejected' && !showForm && (
                <>
                  <Alert severity="error">
                    Your application wasn't approved{application.admin_notes ? `: ${application.admin_notes}` : '.'}
                  </Alert>
                  <Button variant="contained" onClick={() => { setShowForm(true); setFormData({ storeName: '', description: '', contactPhone: '' }); }} sx={{ alignSelf: 'flex-start' }}>
                    Apply Again
                  </Button>
                </>
              )}
            </Stack>
          )}

          {displayForm && (
            <Stack component="form" onSubmit={handleSubmit} spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Store Name" name="storeName" value={formData.storeName} onChange={handleChange} required fullWidth />
              <TextField
                label="Tell us about what you plan to sell" name="description" value={formData.description}
                onChange={handleChange} required fullWidth multiline minRows={4}
                placeholder="What do you sell (RC models, kits, parts, used gear...), roughly how many items, new or used..."
              />
              <TextField label="Contact Phone (optional)" name="contactPhone" value={formData.contactPhone} onChange={handleChange} fullWidth />
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default BecomeSeller;
